import React from 'react';
import { CheckCircle, XCircle } from 'lucide-react';

interface ParteBFormProps {
  data: any;
  onChange: (data: any) => void;
}

export function ParteBForm({ data, onChange }: ParteBFormProps) {
  const safeData = data || {};

  const handleChange = (section: string, field: string, value: any) => {
    const currentSection = safeData[section] || {};
    const newData = {
      ...safeData,
      [section]: {
        ...currentSection,
        [field]: value
      }
    };
    onChange(newData);
  };

  const renderEsito = (esito: string) => {
    if (esito === 'CONFORME') {
      return (
        <div className="flex items-center text-green-600 font-semibold">
          <CheckCircle className="w-5 h-5 mr-2" />
          {esito}
        </div>
      );
    }
    if (esito === 'NON CONFORME') {
      return (
        <div className="flex items-center text-red-600 font-semibold">
          <XCircle className="w-5 h-5 mr-2" />
          {esito}
        </div>
      );
    }
    return <span className="text-gray-400">-</span>;
  };

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      {/* B1 - Validità documento identificativo */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800">B1 - Validità Documento Identificativo</h3>
          {renderEsito(safeData.b1?.esito || '')}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Documenti Identificazione <span className="text-red-500">*</span>
            </label>
            <select
              value={safeData.b1?.documentiIdentificazione || ''}
              onChange={(e) => handleChange('b1', 'documentiIdentificazione', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Seleziona...</option>
              <option value="SI">SI</option>
              <option value="NO">NO</option>
            </select>
            <p className="mt-2 text-sm text-gray-500">
              Verificare la validità e la presenza del documento di identità del contraente
            </p>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
            <textarea
              value={safeData.b1?.note || ''}
              onChange={(e) => handleChange('b1', 'note', e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="Note aggiuntive sul documento di identità..."
            />
          </div>
        </div>
      </div>

      {/* B2 - Documentazione Adeguata Verifica */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800">B2 - Documentazione Adeguata Verifica (AML)</h3>
          {renderEsito(safeData.b2?.esito || '')}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Questionario Adeguata Verifica Presente <span className="text-red-500">*</span>
            </label>
            <select
              value={safeData.b2?.qAdv || ''}
              onChange={(e) => handleChange('b2', 'qAdv', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Seleziona...</option>
              <option value="SI">SI</option>
              <option value="NO">NO</option>
            </select>
            <p className="mt-2 text-sm text-gray-500">
              Verificare la presenza del questionario AML/Adeguata Verifica
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Presenza Firma sul Questionario <span className="text-red-500">*</span>
            </label>
            <select
              value={safeData.b2?.presenzaFirmaQAdv || ''}
              onChange={(e) => handleChange('b2', 'presenzaFirmaQAdv', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Seleziona...</option>
              <option value="SI">SI</option>
              <option value="NO">NO</option>
            </select>
            <p className="mt-2 text-sm text-gray-500">
              Verificare che il questionario sia firmato dal contraente
            </p>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
            <textarea
              value={safeData.b2?.note || ''}
              onChange={(e) => handleChange('b2', 'note', e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="Note aggiuntive sulla documentazione AML..."
            />
          </div>
        </div>

        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-800">
            <strong>Normativa AML:</strong> Verificare che tutta la documentazione necessaria per l'adeguata verifica
            della clientela sia presente e correttamente compilata secondo la normativa antiriciclaggio.
          </p>
        </div>
      </div>
    </div>
  );
}
